import asObject from 'lib/sequence/component/children/asObject'
import { reset } from 'lib/sequence/component/state/value'
import map from 'lib/sequence/model/api/map'
import { asyncPipeSpread, getFirstItem, isArr } from 'lib/util'
import { CONFIRM, SEARCH } from 'app/_shared/events/setStepTab'
import find from 'lib/sequence/component/children/find'

/**
 * If the current model has an organisation, resets the search field.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const resetSearchField = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { organisation } = model[G.CHILDREN]

  const { component } = components
  const { search } = asObject(component[G.CHILDREN])
  const { searchField } = asObject(search[G.CHILDREN])

  organisation[G.CACHE] && reset(searchField)

  return [components, ...args]
}

/**
 * Maps the current model's organization to the location view.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapOrganisation = module => async (components, ...args) => {
  const { component } = components
  const model = module[G.MODEL]
  const { organisation } = model[G.CHILDREN]

  if (organisation[G.CACHE]) {
    const { confirm } = asObject(component[G.CHILDREN])
    const { organisation: organisationView } = asObject(confirm[G.CHILDREN])
    // mapping selected model to the view
    map(organisation)(organisationView)
  }

  return [components, ...args]
}

/**
 * Person organization step action.
 *
 * Handles the display and status of the step's elements.
 *
 * It lets the user select an organization from a list or create a new one by filling a form.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setStepState = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { organisation } = model[G.CHILDREN]

  const { component } = components
  const stepState = component[G.STATE]

  if (organisation[G.CACHE]) {
    const { name, address } = organisation[G.CHILDREN]
    const subtitle = Object.values(address[G.CACHE])
      .map(prop => (prop && isArr(prop) ? getFirstItem(prop)?.value : prop))
      .join(' ')

    stepState.title = name[G.CACHE]
    stepState.subtitle = subtitle
    stepState.completed = true
    stepState.activeTab = CONFIRM
  } else {
    stepState.title = ''
    stepState.subtitle = ''
    stepState.completed = false
    stepState.confirmed = false
    stepState.activeTab = SEARCH
  }

  return args
}

/**
 * Organisation identification step.
 *
 * If no organisation is selected, displays a list to search among them and a button to add a new
 * one. Otherwise, sets the step as complete and displays the selected organisation's data together
 * with buttons to edit it, to clear the confirm view and to go to the next step.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(*=): function(...[*]): Promise<*[]>}
 */
export default module => component => async (...args) => await asyncPipeSpread(
  resetSearchField(module),
  mapOrganisation(module),
  setStepState(module),
)(find(component), ...args)
